import { useState } from 'react'
import toast from 'react-hot-toast'

import { Popover, PopoverButton, Button, PopoverPanel, Switch } from '@headlessui/react'
import { ShareIcon } from '@heroicons/react/16/solid'

import { useRecipeQuery, useShareRecipeMutation, useUnshareRecipeMutation } from '@/recipe/api'

export type RecipeViewProps = {
  id?: string
  shareToken?: string
}
export const RecipeView = ({ id, shareToken }: RecipeViewProps) => {
  const { data: recipe } = useRecipeQuery({ id, shareToken })
  const [shared, setShared] = useState(!!recipe?.shareToken)

  const { mutateAsync: shareRecipe } = useShareRecipeMutation()
  const { mutateAsync: unshareRecipe } = useUnshareRecipeMutation()

  const shareLink = `${location.origin}/recipe/${recipe?.shareToken}`

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4">
      {id && (
        <Popover>
          <PopoverButton className="absolute top-4 right-4 block text-sm/6 font-semibold text-white/50 focus:outline-none data-active:text-white data-focus:outline data-focus:outline-white data-hover:text-white">
            <ShareIcon className="size-6" />
          </PopoverButton>
          <PopoverPanel
            transition
            anchor="bottom end"
            className="z-10 w-80 rounded-xl bg-neutral-900/95 p-3 text-sm/6 transition duration-200 ease-in-out [--anchor-gap:--spacing(5)] data-closed:-translate-y-1 data-closed:opacity-0"
          >
            <div className="flex items-center gap-2 p-3">
              <p className="text-white">Share public link</p>
              <Switch
                checked={shared}
                onChange={async (checked) => {
                  if (checked) {
                    await shareRecipe({ id })
                  } else {
                    await unshareRecipe({ id })
                  }
                  setShared(checked)
                }}
                className="group relative flex h-7 w-14 cursor-pointer rounded-full bg-white/10 p-1 ease-in-out focus:not-data-focus:outline-none data-checked:bg-white/10 data-focus:outline data-focus:outline-white"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none inline-block size-5 translate-x-0 rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out group-data-checked:translate-x-7"
                />
              </Switch>
            </div>
            {shared && (
              <div className="flex items-center justify-between gap-2 rounded-xl bg-white/10 p-2">
                <p className="overflow-hidden text-ellipsis text-white">{shareLink}</p>
                <Button
                  className="rounded-md bg-white/10 px-2 py-0.5 text-sm/6 font-semibold text-white shadow-inner shadow-white/10 focus:not-data-focus:outline-none data-focus:outline data-focus:outline-white data-hover:bg-white/20 data-open:bg-white/10"
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink)
                    toast.success('Copied!')
                  }}
                >
                  Copy
                </Button>
              </div>
            )}
          </PopoverPanel>
        </Popover>
      )}
      <div>
        <h1 className="text-2xl font-bold">{recipe.title}</h1>
        <a
          href={recipe.video.url}
          className="text-sm text-blue-600 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Watch video
        </a>
      </div>

      <div>
        <h2 className="text-lg font-semibold">Ingredients</h2>
        <ul className="ml-5 list-disc">
          {recipe.ingredients.map((ing, idx) => (
            <li key={idx}>
              {ing.amount} — {ing.name}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h2 className="text-lg font-semibold">Steps</h2>
        <ol className="ml-5 list-decimal space-y-2">
          {recipe.steps.map((step) => (
            <li key={step.order}>
              {step.action}
              {step.image && <img src={step.image} alt={`Step ${step.order}`} className="mt-1 rounded" />}
            </li>
          ))}
        </ol>
      </div>

      <div>
        <h2 className="text-lg font-semibold">Nutrition</h2>
        <ul className="space-y-1 text-sm text-gray-300">
          <li>Calories: {recipe.nutrition.calories}</li>
          <li>Protein: {recipe.nutrition.protein}g</li>
          <li>Fat: {recipe.nutrition.fat}g</li>
          <li>Carbs: {recipe.nutrition.carbs}g</li>
        </ul>
      </div>
    </div>
  )
}

export default RecipeView
